<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { useAuth } from '@/composables/useAuth'
import { resetPasswordSchema, getSafeRedirectPath, type ResetPasswordFormData } from '@/utils/auth/validation'
import { toast } from '@/components/ui/toast/use-toast'
import { handleAuthError } from '@/utils/auth/errorMapping'
import EmailAuthForm from './components/EmailAuthForm.vue'
import PhoneAuthForm from './components/PhoneAuthForm.vue'
import GoogleAuthButton from './components/GoogleAuthButton.vue'
import PhoneAuthButton from './components/PhoneAuthButton.vue'
import AuthDivider from './components/AuthDivider.vue'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

const router = useRouter()
const route = useRoute()
const store = useStore()
const { sendPasswordReset, isLoading, clearError } = useAuth()

const user = computed(() => store.getters.getUser)

// Auth mode state
type AuthMode = 'signin' | 'reset'
const authMode = ref<AuthMode>('signin')

// Auth method state (default vs email/phone forms)
const showEmailForm = ref(false)
const showPhoneForm = ref(false)
const emailFormStep = ref('email')

/**
 * Switch between auth modes
 */
const switchMode = (mode: AuthMode) => {
  authMode.value = mode
  showEmailForm.value = false
  showPhoneForm.value = false
  clearError()
}

/**
 * Toggle email form visibility
 */
const toggleEmailForm = () => {
  showEmailForm.value = !showEmailForm.value
  showPhoneForm.value = false
  emailFormStep.value = 'email'
  clearError()
}

/**
 * Toggle phone form visibility
 */
const togglePhoneForm = () => {
  showPhoneForm.value = !showPhoneForm.value
  showEmailForm.value = false
  clearError()
}

/**
 * Handle successful authentication from any method
 * Flow: Get Firebase Token → Exchange with Backend (via Vuex)
 */
const handleAuthSuccess = async (token: string) => {
  console.log('[AuthPage] Authentication successful, exchanging token with backend...')

  try {
    // Exchange Firebase token for backend JWT tokens
    const result = await store.dispatch('user/handleAuthentication', {
      firebaseToken: token
    })

    if (result.success) {
      console.log('[AuthPage] Backend authentication successful')
      const redirectPath = getSafeRedirectPath(route.query.redirect)
      if (redirectPath) {
        router.push(redirectPath)
        return
      }
      // App.vue will handle navigation after auth state changes
    } else {
      // Check if user is not registered
      if (result.error === 'USER_NOT_REGISTERED') {
        const redirectPath = getSafeRedirectPath(route.query.redirect)
        if (redirectPath) {
          // Invite flow: user just created Firebase account, redirect to invite page
          console.log('[AuthPage] User not registered, redirecting to invite flow:', redirectPath)
          router.push(redirectPath)
          return
        }
        console.log('[AuthPage] User not registered, letting App.vue handle routing')
        return
      }

      // Check if email is not verified
      if (result.error === 'EMAIL_NOT_VERIFIED') {
        console.log('[AuthPage] Email not verified')
        toast({
          title: 'Email Not Verified',
          description: 'Please check your email and click the verification link before signing in.',
          variant: 'default'
        })
        return
      }

      console.error('[AuthPage] Backend authentication failed:', result.reason)
      const errorMessage = handleAuthError(result.reason || new Error('Backend authentication failed'), 'backend')
      toast({
        title: 'Authentication failed',
        description: errorMessage,
        variant: 'destructive'
      })
    }
  } catch (error: any) {
    console.error('[AuthPage] Authentication error:', error)

    // Check for USER_NOT_REGISTERED in error response
    if (error?.response?.data?.detail?.error === 'USER_NOT_REGISTERED') {
      const redirectPath = getSafeRedirectPath(route.query.redirect)
      if (redirectPath) {
        console.log('[AuthPage] User not registered (from error), redirecting to invite flow:', redirectPath)
        router.push(redirectPath)
        return
      }
      console.log('[AuthPage] User not registered (from error), letting App.vue handle routing')
      return
    }

    // Check for EMAIL_NOT_VERIFIED in error response
    if (error?.response?.data?.detail?.error === 'EMAIL_NOT_VERIFIED') {
      console.log('[AuthPage] Email not verified (from error)')
      toast({
        title: 'Email Not Verified',
        description: error?.response?.data?.detail?.message || 'Please check your email and click the verification link before signing in.',
        variant: 'default'
      })
      return
    }

    const errorMessage = handleAuthError(error, 'backend')
    toast({
      title: 'Authentication failed',
      description: errorMessage,
      variant: 'destructive'
    })
  }
}

/**
 * Track email form step for back button text
 */
const handleEmailStepChange = (step: string) => {
  emailFormStep.value = step
}

/**
 * Display authentication error message
 */
const showAuthError = (message: string) => {
  toast({
    title: 'Sign in failed',
    description: message,
    variant: 'destructive'
  })
}

/**
 * Password Reset Form
 */
const onPasswordReset = async (values: Record<string, any>) => {
  const typedValues = values as ResetPasswordFormData
  const success = await sendPasswordReset(typedValues.email)

  if (success) {
    toast({
      title: 'Password reset email sent',
      description: 'Check your email for instructions to reset your password'
    })
    switchMode('signin')
  } else {
    toast({
      title: 'Failed to send reset email',
      description: 'Please try again or contact support',
      variant: 'destructive'
    })
  }
}
</script>

<template>
  <!-- Full screen layout, grid only on large screens -->
  <div v-show="!user" class="h-screen w-screen bg-background lg:grid lg:grid-cols-[1fr_auto]">
    <!-- Left Side - Image (only visible on large screens) -->
    <div class="hidden lg:flex bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-neutral-900 dark:to-neutral-800 items-center justify-center p-16">
      <img
        src="@/assets/img/earth-art.png"
        alt="Earth"
        class="w-full h-auto object-contain"
      />
    </div>

    <!-- Right Side - Auth Form -->
    <div class="h-screen w-screen lg:w-[500px] flex flex-col items-center justify-center px-8 bg-background">
      <div class="w-full max-w-sm space-y-6">
        <!-- Header -->
        <div class="text-center space-y-2">
          <h1 class="text-3xl font-bold tracking-tight text-foreground">
            <span v-if="authMode === 'signin'">Welcome back</span>
            <span v-else>Reset password</span>
          </h1>
          <p class="text-sm text-muted-foreground">
            <span v-if="authMode === 'signin'">Sign in to your account to continue</span>
            <span v-else>Enter your email to receive a password reset link</span>
          </p>
        </div>

        <!-- Sign In / Sign Up Mode -->
        <div v-if="authMode !== 'reset'">
          <!-- Default View: Show all auth options -->
          <div v-if="!showEmailForm && !showPhoneForm" class="space-y-4">
            <!-- Google Auth Button (Primary) -->
            <GoogleAuthButton
              @success="handleAuthSuccess"
              @error="showAuthError"
            />

            <!-- Sign in with Email Button -->
            <Button
              @click="toggleEmailForm"
              variant="outline"
              class="w-full"
            >
              Sign in using email
            </Button>

            <!-- Sign in with Phone Button -->
            <PhoneAuthButton
              @click="togglePhoneForm"
            />

            <!-- Sign Up Link -->
            <div class="text-center pt-4 border-t">
              <span class="text-sm text-muted-foreground">Don't have an account? </span>
              <Button
                variant="link"
                class="p-0 h-auto text-sm"
                @click="router.push('/sign-up')"
              >
                Sign up
              </Button>
            </div>
          </div>

          <!-- Email Form View -->
          <div v-else-if="showEmailForm">
            <!-- Back to main options -->
            <div class="text-center mb-6">
              <button
                @click="toggleEmailForm"
                class="text-sm text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1"
              >
                <span>←</span>
                <span>Back to all sign in options</span>
              </button>
            </div>

            <!-- Email/Password Form -->
            <EmailAuthForm
              @success="handleAuthSuccess"
              @error="showAuthError"
              @forgot-password="switchMode('reset')"
              @step-change="handleEmailStepChange"
            />
          </div>

          <!-- Phone Form View -->
          <div v-else-if="showPhoneForm">
            <!-- Back to main options -->
            <div class="text-center mb-6">
              <button
                @click="togglePhoneForm"
                class="text-sm text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1"
              >
                <span>←</span>
                <span>Back to all sign in options</span>
              </button>
            </div>

            <!-- Phone Auth Form -->
            <PhoneAuthForm
              @success="handleAuthSuccess"
              @error="showAuthError"
            />
          </div>
        </div>

        <!-- Password Reset Mode -->
        <div v-else>
          <Form
            :validation-schema="toTypedSchema(resetPasswordSchema)"
            @submit="onPasswordReset"
            class="space-y-4"
            :validate-on-blur="true"
            :validate-on-change="false"
            :validate-on-input="false"
            :validate-on-model-update="false"
          >
            <!-- Email Field -->
            <FormField name="email" v-slot="{ componentField }">
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    autocomplete="email"
                    v-bind="componentField"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <!-- Submit Button -->
            <Button
              type="submit"
              :disabled="isLoading"
              class="w-full"
            >
              <span v-if="isLoading">Sending...</span>
              <span v-else>Send reset link</span>
            </Button>
          </Form>

          <!-- Back to Sign In -->
          <div class="text-center pt-4">
            <button
              @click="switchMode('signin'); showEmailForm = false"
              class="text-sm text-primary hover:text-primary/80 hover:underline"
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- After Login - Organization Selection -->
  <div v-show="user" class="min-h-screen flex items-center justify-center p-8 bg-background">
    <div class="w-full max-w-md">
      <router-view></router-view>
    </div>
  </div>
</template>

<style scoped>
/* Additional styles if needed */
</style>
